/**
 * @file ReceiptPlacementButton.tsx
 * @description Кнопка «Разместить по ячейкам» в шапке проведённой приёмки
 * (phase19.23 этап B, W14).
 *
 * Сервер собирает черновик перемещения со всеми партиями приёмки, которым ещё
 * не назначен адрес, и подставляет ячейки по умолчанию у фасовок. Повторное
 * нажатие безопасно: документ не дублируется, недостающие партии дописываются
 * в существующий черновик — поэтому кнопку можно оставить на экране навсегда.
 *
 * Пустого документа больше не бывает: раскладывать нечего — сервер отвечает
 * `data: null` и перечисляет позиции без партии (правка контракта
 * 2026-09-20). Тогда никуда не уходим, а объясняем тостом: переход в пустой
 * черновик выглядел как «система завела мусорный документ».
 */

import { Loader2, PackageOpen } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";

import { Button } from "../../ui/button";
import { useNotify } from "../../ui/NotifyProvider";
import { nothingToPlaceToast } from "../document-texts";
import { transfersApi } from "../../../lib/api-transfers";
import { wmsErrorText } from "../../../lib/wms-error-text";

interface Props {
  receiptId: string;
  /**
   * Размещать нечего (все позиции без партии) — кнопку гасим, причина стоит
   * подписью под ней (`DocumentHeaderActions.hint`), а не в тосте после клика.
   */
  disabled?: boolean;
}

export function ReceiptPlacementButton({ receiptId, disabled = false }: Props) {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const notify = useNotify();
  const [busy, setBusy] = useState(false);

  async function handleClick() {
    setBusy(true);
    try {
      const result = await transfersApi.createPlacementDraft(receiptId);
      if (!result.transfer) {
        notify.error(nothingToPlaceToast(result.unplaceableItems));
        return;
      }
      navigate(`/shop/${slug}/wms/transfers/${result.transfer.id}`);
    } catch (err) {
      notify.error(wmsErrorText(err, "Не удалось собрать лист размещения"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button
      variant="outline"
      disabled={busy || disabled}
      onClick={() => void handleClick()}
    >
      {busy ? (
        <Loader2 className="size-4 animate-spin" />
      ) : (
        <PackageOpen className="size-4" />
      )}
      <span className="ml-2">{busy ? "Собираем…" : "Разместить по ячейкам"}</span>
    </Button>
  );
}
